import {Activity, ArrowUpRight, Layers} from 'lucide-react'
import {useMemo} from 'react'
import type {LogsStats} from './types'

const DAY_MS = 24 * 60 * 60 * 1000
const numberFormat = new Intl.NumberFormat('pt-BR')
const percentageFormat = new Intl.NumberFormat('pt-BR', {maximumFractionDigits: 1})
const dateFormat = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  timeZone: 'UTC',
})

export interface ActivityBucket {
  startDate: string
  endDate: string
  count: number
  days: number
}

/** Calendar buckets keep zero-activity days and all totals without rendering an unbounded chart. */
export const buildActivityBuckets = (
  dailyStats: LogsStats['dailyStats'],
  maxColumns = 32
): ActivityBucket[] => {
  const dailyCounts = new Map<number, number>()
  dailyStats.forEach((day) => {
    const calendarDate = String(day.log_date).slice(0, 10)
    if (!/^\d{4}-\d{2}-\d{2}$/.test(calendarDate)) return
    const timestamp = Date.parse(`${calendarDate}T00:00:00Z`)
    const count = Number(day.daily_count)
    if (!Number.isFinite(timestamp) || !Number.isFinite(count) || count < 0) return
    dailyCounts.set(timestamp, (dailyCounts.get(timestamp) || 0) + count)
  })
  const dates = Array.from(dailyCounts.keys()).sort((a, b) => a - b)
  if (!dates.length) return []

  const firstDate = dates[0]
  const lastDate = dates[dates.length - 1]
  const totalDays = Math.round((lastDate - firstDate) / DAY_MS) + 1
  const columnLimit = Number.isFinite(maxColumns) ? Math.max(1, Math.floor(maxColumns)) : 32
  const bucketDays = Math.ceil(totalDays / columnLimit)
  const bucketCount = Math.ceil(totalDays / bucketDays)
  const buckets = Array.from({length: bucketCount}, (_, index) => {
    const start = firstDate + index * bucketDays * DAY_MS
    const end = Math.min(lastDate, start + (bucketDays - 1) * DAY_MS)
    return {
      startDate: new Date(start).toISOString().slice(0, 10),
      endDate: new Date(end).toISOString().slice(0, 10),
      count: 0,
      days: Math.round((end - start) / DAY_MS) + 1,
    }
  })
  dailyCounts.forEach((count, timestamp) => {
    const index = Math.floor((timestamp - firstDate) / (bucketDays * DAY_MS))
    buckets[index].count += count
  })
  return buckets
}

const formatDay = (value: string) => dateFormat.format(new Date(`${value}T00:00:00Z`))
const bucketLabel = (bucket: ActivityBucket) =>
  bucket.startDate === bucket.endDate
    ? formatDay(bucket.startDate)
    : `${formatDay(bucket.startDate)} a ${formatDay(bucket.endDate)}`

interface LogsOverviewProps {
  stats: LogsStats
  onTypeSelect: (id: number) => void
}

export default function LogsOverview({stats, onTypeSelect}: LogsOverviewProps) {
  const buckets = useMemo(() => buildActivityBuckets(stats.dailyStats), [stats.dailyStats])
  const types = useMemo(
    () => [...stats.typeDistribution].sort((a, b) => b.count - a.count),
    [stats.typeDistribution]
  )
  const peak = Math.max(1, ...buckets.map((bucket) => bucket.count))
  const chartCeiling = Math.max(2, Math.ceil(peak / 2) * 2)
  const chartTotal = buckets.reduce((sum, bucket) => sum + bucket.count, 0)
  const isGrouped = buckets.some((bucket) => bucket.days > 1)
  const total = stats.summary.total_logs

  return (
    <div className='logs-overview-grid'>
      <section className='logs-panel' aria-labelledby='logs-activity-title'>
        <div className='logs-panel-header'>
          <div>
            <span className='logs-eyebrow'>
              <Activity size={14} aria-hidden='true' /> Evolução dos registros
            </span>
            <h2 id='logs-activity-title'>Atividade ao longo do tempo</h2>
            <p className='logs-muted'>
              {isGrouped
                ? 'Períodos agrupados para facilitar a leitura. Todos os registros estão incluídos.'
                : 'Registros por dia, considerando os filtros aplicados.'}
            </p>
          </div>
          <span className='logs-chart-total'>
            {numberFormat.format(chartTotal)} <small>registros</small>
          </span>
        </div>

        {buckets.length ? (
          <>
            <div className='logs-chart-plot'>
              <div className='logs-chart-y-axis' aria-hidden='true'>
                <span>{numberFormat.format(chartCeiling)}</span>
                <span>{numberFormat.format(chartCeiling / 2)}</span>
                <span>0</span>
              </div>
              <div
                className='logs-activity-chart'
                role='img'
                aria-label={`Atividade de ${formatDay(buckets[0].startDate)} a ${formatDay(
                  buckets[buckets.length - 1].endDate
                )}: ${numberFormat.format(
                  chartTotal
                )} registros. Os valores de cada período estão disponíveis em Ver dados do gráfico.`}
              >
                {buckets.map((bucket) => (
                  <div
                    className='logs-activity-column'
                    key={bucket.startDate}
                    title={`${bucketLabel(bucket)}: ${numberFormat.format(bucket.count)} registros`}
                    aria-hidden='true'
                  >
                    <span className='logs-activity-value'>{numberFormat.format(bucket.count)}</span>
                    <div
                      className={`logs-activity-bar${
                        bucket.count === 0 ? ' logs-activity-bar-empty' : ''
                      }`}
                      style={{
                        height: `${(bucket.count / chartCeiling) * 100}%`,
                        minHeight: bucket.count > 0 ? 4 : 1,
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
            <div className='logs-chart-axis' aria-hidden='true'>
              <span>{formatDay(buckets[0].startDate)}</span>
              <span>
                {isGrouped ? `Até ${buckets[0].days} dias por coluna` : '1 dia por coluna'}
              </span>
              <span>{formatDay(buckets[buckets.length - 1].endDate)}</span>
            </div>
            <details className='logs-chart-data'>
              <summary>Ver dados do gráfico</summary>
              <div className='table-responsive'>
                <table className='table align-middle'>
                  <caption className='visually-hidden'>
                    Registros por período nos filtros atuais
                  </caption>
                  <thead>
                    <tr>
                      <th scope='col'>Período</th>
                      <th scope='col' className='text-end'>
                        Registros
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {buckets.map((bucket) => (
                      <tr key={bucket.startDate}>
                        <th scope='row'>{bucketLabel(bucket)}</th>
                        <td className='text-end'>{numberFormat.format(bucket.count)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          </>
        ) : (
          <div className='logs-empty-state'>
            <Activity size={30} aria-hidden='true' />
            <strong>Nenhuma atividade no período</strong>
            <span>Ajuste os filtros para explorar os registros.</span>
          </div>
        )}
      </section>

      <section className='logs-panel' aria-labelledby='logs-types-title'>
        <div className='logs-panel-header'>
          <div>
            <span className='logs-eyebrow'>
              <Layers size={14} aria-hidden='true' /> Composição da atividade
            </span>
            <h2 id='logs-types-title'>Registros por tipo</h2>
            <p className='logs-muted'>Selecione um tipo para filtrar a análise.</p>
          </div>
        </div>
        {types.length ? (
          <div className='logs-type-list'>
            {types.map((type) => {
              const share = total > 0 ? Math.min(100, Math.max(0, (type.count / total) * 100)) : 0
              return (
                <button
                  type='button'
                  className='logs-type-row'
                  key={type.id_log_type}
                  onClick={() => onTypeSelect(type.id_log_type)}
                  aria-label={`Filtrar por ${type.type}: ${numberFormat.format(
                    type.count
                  )} registros`}
                >
                  <span className='logs-type-row-heading'>
                    <span className='logs-type-name' title={type.type}>
                      {type.type}
                    </span>
                    <strong className='logs-type-count'>{numberFormat.format(type.count)}</strong>
                    <span className='logs-type-percent'>
                      {share > 0 && share < 0.1 ? '< 0,1' : percentageFormat.format(share)}%
                    </span>
                    <ArrowUpRight className='logs-type-arrow' size={14} aria-hidden='true' />
                  </span>
                  <span className='logs-type-track' aria-hidden='true'>
                    <span className='logs-type-fill' style={{width: `${share}%`}} />
                  </span>
                </button>
              )
            })}
          </div>
        ) : (
          <div className='logs-empty-state'>
            <Layers size={30} aria-hidden='true' />
            <strong>Nenhum tipo registrado</strong>
            <span>Os tipos aparecerão conforme os filtros selecionados.</span>
          </div>
        )}
      </section>
    </div>
  )
}
